import { useMemo, useState } from "react";
import { Check, Search, X } from "lucide-react";
import { cn } from "./ui";
import { PaneHead } from "./settings-parts";
import { channelOptions, type ChannelOption } from "@/mocks/settings";
import ChannelWizard, { type WizardChannel } from "./channel-wizard";
import ChannelPicker, { type ChannelPick, type PickerFamily } from "./channel-picker";
import { CHANNEL_META, WIZARD_CATALOG_ID } from "./channel-wizard-kit";
import { t } from "@/lib/i18n";

/** Каналы с несколькими типами подключения: сразу открываем выбор типа. */
const FAMILIES: Record<string, PickerFamily> = {
  whatsapp: "whatsapp",
  telegram: "telegram",
  viber: "viber",
  instagram: "instagram",
};

/* Раздел «Каналы» в настройках: пустое состояние, поиск и окно добавления канала.
   Подключение имитируется в макете — реальные аккаунты не затрагиваются. */

type Connected = ChannelOption & { account: string };

export default function SettingsChannels({ onFlash }: { onFlash: (text: string) => void }) {
  const [channels, setChannels] = useState<Connected[]>([]);
  const [query, setQuery] = useState("");
  const [picking, setPicking] = useState(false);
  const [pickQuery, setPickQuery] = useState("");
  const [wizard, setWizard] = useState<WizardChannel | null>(null);
  const [picker, setPicker] = useState<PickerFamily | null>(null);

  const available = useMemo(() => {
    const q = pickQuery.trim().toLowerCase();
    return channelOptions
      .filter((c) => !channels.some((x) => x.id === c.id || x.id.startsWith(`${c.id}-`)))
      .filter((c) => (q ? `${c.name} ${c.category} ${c.hint}`.toLowerCase().includes(q) : true));
  }, [channels, pickQuery]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? channels.filter((c) => `${c.name} ${c.account}`.toLowerCase().includes(q)) : channels;
  }, [channels, query]);

  const add = (c: ChannelOption) => {
    const family = FAMILIES[c.id];
    if (family) {
      setPicker(family);
      return;
    }
    setChannels((prev) => [...prev, { ...c, account: t("Основной аккаунт компании") }]);
    setPicking(false);
    setPickQuery("");
    onFlash(t("Канал «{name}» подключён — диалоги уже идут во «Входящие»", { name: c.name }));
  };

  const pickKind = (kind: ChannelPick) => {
    setPicker(null);
    setWizard(kind);
  };

  const connectFromWizard = (account: string) => {
    if (!wizard) return;
    const kind = wizard;
    const id = WIZARD_CATALOG_ID[kind];
    const option = channelOptions.find((c) => c.id === id);
    const name = CHANNEL_META[kind].name;
    setChannels((prev) => [
      ...prev.filter((x) => x.id !== id && !x.id.startsWith(id)),
      {
        ...(option ?? { id, name, icon: "ri-link", category: t("Мессенджер"), hint: "" }),
        id: `${id}-${kind}`,
        name,
        icon: CHANNEL_META[kind].icon,
        account,
      },
    ]);
    setPicking(false);
    setPickQuery("");
    onFlash(t("Канал «{name}» подключён — диалоги уже идут во «Входящие»", { name }));
  };

  const remove = (c: Connected) => {
    setChannels((prev) => prev.filter((x) => x.id !== c.id));
    onFlash(t("Канал «{name}» отключён, история диалогов сохранена", { name: c.name }));
  };

  return (
    <div className="relative min-h-[26rem] p-3.5 md:p-5">
      <PaneHead
        icon="ri-share-forward-line"
        title={t("Каналы")}
        text={t("Управляйте своими каналами обмена сообщениями и открывайте новые, которые помогут вам привлечь больше клиентов.")}
      />

      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
        <button
          type="button"
          onClick={() => {
            setPicking(true);
            setPickQuery("");
          }}
          className="inline-flex h-9 flex-none cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-3.5 text-[12.5px] font-semibold text-primary-foreground transition hover:brightness-110"
        >
          <i className="ri-add-line text-sm leading-none" aria-hidden />
          {t("Добавить канал")}
        </button>
        <div className="relative sm:ms-auto sm:w-56">
          <Search className="pointer-events-none absolute start-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("Поиск канала")}
            aria-label={t("Поиск канала")}
            className="h-9 w-full box-border rounded-btn border border-border bg-background ps-8 pe-3 text-[12px] text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>
      </div>

      {channels.length === 0 ? (
        <div className="mt-8 flex flex-col items-center px-3 pb-6 pt-6 text-center">
          <div className="relative mx-auto size-16" aria-hidden>
            <span className="absolute inset-0 translate-x-2 translate-y-2 rounded-2xl border border-border bg-background/40" />
            <span className="relative grid size-14 place-items-center rounded-2xl border border-border bg-background/70 text-2xl leading-none text-muted-foreground">
              <i className="ri-share-forward-line" />
            </span>
          </div>
          <h4 className="mt-5 text-[14px] font-semibold">{t("Каналы не подключены")}</h4>
          <p className="mx-auto mt-1.5 max-w-md text-[12px] leading-relaxed text-muted-foreground">
            {t("В этом рабочем пространстве пока нет подключенных каналов. Свяжитесь с владельцем рабочего пространства, чтобы добавить его.")}
          </p>
          <button
            type="button"
            onClick={() => setPicking(true)}
            className="mt-4 inline-flex h-8 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn border border-border px-3 text-[11.5px] font-semibold transition hover:border-primary/50 hover:text-primary"
          >
            <i className="ri-add-line text-[13px] leading-none" aria-hidden />
            {t("Добавить первый канал")}
          </button>
        </div>
      ) : visible.length === 0 ? (
        <div className="mt-8 rounded-btn border border-dashed border-border bg-background/40 px-4 py-10 text-center">
          <p className="text-[13px] font-semibold">{t("По запросу «{query}» каналов не нашлось", { query: query.trim() })}</p>
          <p className="mt-1 text-[12px] text-muted-foreground">{t("Проверьте название или очистите поиск.")}</p>
          <button
            type="button"
            onClick={() => setQuery("")}
            className="mt-3 inline-flex h-8 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-btn border border-border px-3 text-[11.5px] font-semibold transition hover:border-primary/50 hover:text-primary"
          >
            <i className="ri-refresh-line text-[13px] leading-none" aria-hidden />
            {t("Очистить поиск")}
          </button>
        </div>
      ) : (
        <ul className="mt-4 flex flex-col gap-2">
          {visible.map((c) => (
            <li
              key={c.id}
              className="flex flex-wrap items-center gap-3 rounded-btn border border-border bg-background/50 p-3 transition hover:border-primary/40"
            >
              <span className="grid size-9 flex-none place-items-center rounded-btn bg-primary/10 text-base leading-none text-primary" aria-hidden>
                <i className={c.icon} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="flex flex-wrap items-center gap-2 text-[12.5px] font-semibold leading-snug">
                  {c.name}
                  <span className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-1.5 py-[1px] text-[9.5px] font-semibold text-primary">
                    <Check className="size-2.5" strokeWidth={3} aria-hidden />
                    {t("Подключён")}
                  </span>
                </p>
                <p className="mt-0.5 truncate text-[10.5px] text-muted-foreground">
                  {c.category} · {c.account}
                </p>
              </div>
              <button
                type="button"
                onClick={() => remove(c)}
                className="inline-flex h-7 flex-none cursor-pointer items-center whitespace-nowrap rounded-btn border border-border px-2.5 text-[10.5px] font-semibold text-muted-foreground transition hover:border-red-400/60 hover:text-red-500"
              >
                {t("Отключить")}
              </button>
            </li>
          ))}
        </ul>
      )}

      <p className="mt-3 border-t border-border/60 pt-3 text-[11px] text-muted-foreground">
        {channels.length > 0
          ? t("Подключено каналов: {n}. Демо-режим: подключение живёт только на этой странице.", { n: channels.length })
          : t("Демо-режим: подключение имитируется в макете и не затрагивает реальные аккаунты.")}
      </p>

      {wizard ? (
        <ChannelWizard channel={wizard} onClose={() => setWizard(null)} onConnected={connectFromWizard} />
      ) : null}

      {picker ? (
        <ChannelPicker family={picker} onPick={pickKind} onClose={() => setPicker(null)} />
      ) : null}

      {picking ? (
        <div className="absolute inset-0 z-30 flex items-end justify-center bg-black/55 p-3 backdrop-blur-sm sm:items-center">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="settings-channel-dialog"
            className="relative flex max-h-full w-full max-w-md flex-col overflow-hidden rounded-card border border-border bg-card shadow-lift"
          >
            <div className="flex items-start gap-2 border-b border-border p-3.5">
              <div className="min-w-0 flex-1">
                <h4 id="settings-channel-dialog" className="text-[13px] font-semibold">
                  {t("Добавить канал")}
                </h4>
                <p className="mt-0.5 text-[10.5px] text-muted-foreground">{t("Выберите, откуда приходят сообщения клиентов.")}</p>
              </div>
              <button
                type="button"
                onClick={() => setPicking(false)}
                aria-label={t("Закрыть окно добавления канала")}
                className="grid size-7 flex-none cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-foreground"
              >
                <X className="size-3.5" aria-hidden />
              </button>
            </div>

            <div className="border-b border-border p-2.5">
              <div className="relative">
                <Search className="pointer-events-none absolute start-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
                <input
                  autoFocus
                  value={pickQuery}
                  onChange={(e) => setPickQuery(e.target.value)}
                  placeholder={t("Найти канал")}
                  aria-label={t("Найти канал")}
                  className="h-8 w-full box-border rounded-btn border border-border bg-background ps-8 pe-3 text-[11.5px] text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
                />
              </div>
            </div>

            <ul className="max-h-72 overflow-y-auto p-1.5">
              {available.length === 0 ? (
                <li className="px-2.5 py-6 text-center text-[11.5px] text-muted-foreground">
                  {pickQuery.trim() ? t("Ничего не нашли — измените запрос") : t("Все каналы уже подключены")}
                </li>
              ) : (
                available.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => add(c)}
                      className="flex w-full cursor-pointer items-center gap-2.5 rounded-btn px-2 py-2 text-start transition hover:bg-muted"
                    >
                      <span className="grid size-8 flex-none place-items-center rounded-btn bg-primary/10 text-sm leading-none text-primary" aria-hidden>
                        <i className={c.icon} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[12px] font-semibold leading-snug">{c.name}</span>
                        <span className="block truncate text-[10px] text-muted-foreground">
                          {c.category} · {c.hint}
                        </span>
                      </span>
                      <i className="ri-add-line flex-none text-[14px] leading-none text-primary" aria-hidden />
                    </button>
                  </li>
                ))
              )}
            </ul>

            <div className="flex items-center justify-between gap-2 border-t border-border px-3.5 py-2.5">
              <span className="text-[10px] text-muted-foreground">{t("Доступно: {n}", { n: available.length })}</span>
              <button
                type="button"
                onClick={() => setPicking(false)}
                className="inline-flex h-7 cursor-pointer items-center whitespace-nowrap rounded-btn border border-border px-3 text-[11px] font-semibold text-muted-foreground transition hover:text-foreground"
              >
                {t("Отмена")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}